import type { ActiveCallObservedSession, ActiveCallsResponse } from '@sbclite/core/sip/active-calls'

type ActiveCallsData = ActiveCallsResponse['data']

export function formatActiveCallAge(timestamp: string | null, now = Date.now()): string {
  if (!timestamp) return 'unknown'
  const milliseconds = now - Date.parse(timestamp)
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return 'unknown'
  const seconds = Math.floor(milliseconds / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** A runtime count is deliberately not shown when every runtime source is unavailable. */
export function activeSessionCountLabel(data: ActiveCallsData | undefined): string {
  if (isRuntimeUnavailable(data)) return 'Unavailable'
  return data?.summary.activeSessions === null || data?.summary.activeSessions === undefined
    ? 'Unknown'
    : String(data.summary.activeSessions)
}

export function isRuntimeUnavailable(data: ActiveCallsData | undefined): boolean {
  if (!data) return false
  return data.freshness === 'unavailable'
    || (data.engines.length > 0 && data.engines.every((engine) => engine.state === 'unavailable'))
}

export function hasCachedSnapshot(data: ActiveCallsData | undefined): boolean {
  return data !== undefined
}

/** Duration is frozen at the engine observation, rather than implying the call remains alive. */
export function formatDurationAtObservation(session: ActiveCallObservedSession): string {
  const start = session.phase === 'connected' && session.establishedAt ? session.establishedAt : session.createdAt
  const observedAt = Date.parse(session.observedAt)
  const startedAt = Date.parse(start)
  if (!Number.isFinite(startedAt) || !Number.isFinite(observedAt) || observedAt < startedAt) return 'unknown'
  return formatActiveCallAge(start, observedAt)
}

export function phaseCountLabel(data: ActiveCallsData): string {
  return data.summary.truncated ? 'Listed sessions only' : 'Sessions'
}
